/**
 * Upload-teller voor de media-wachtrij, in de app-header dus op élke pagina.
 *
 * Waarom dit bestaat: er was al een teller, maar alleen op het route-scherm van
 * de chauffeur (`RouteDetailPage`). De wasserij-pagina's (voorinspectie,
 * reinigen, foto-tijdens, na-inspectie) openen dat scherm nooit, en dat is
 * precies waar de meeste foto's geschoten worden. Op 15-08 bleef daardoor een
 * hele zaterdagploeg (48 kleden) twee dagen op één toestel staan zonder dat
 * iemand het zag. De serverkant-detector hiervoor zit in key-metrics
 * (uploadlag + kleden-zonder-foto's).
 *
 * Deze teller doet meer dan tellen: hij zegt wanneer de stapel te oud is om
 * nog normaal te zijn, want een oplopend getal alleen leest als "hij is bezig".
 */

import { useEffect, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import clsx from 'clsx';
import { getMediaQueueStand, getOpslagRuimte } from '../lib/media/queue';

/** Boven deze leeftijd is de wachtrij geen upload meer maar een stapel. */
const WAARSCHUW_VANAF_MS = 30 * 60_000;

/** De leeftijd loopt op zonder dat de tabel wijzigt, dus die tikt apart. */
const TIK_MS = 30_000;

export function MediaUploadTeller() {
  // useLiveQuery reageert op de tabel zelf: geen poll-interval nodig, en de
  // teller springt direct op nul zodra de laatste rij geland is.
  const stand = useLiveQuery(() => getMediaQueueStand(), [], null);
  const [, tik] = useState(0);
  const [opslagVol, setOpslagVol] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => tik((n) => n + 1), TIK_MS);
    return () => clearInterval(timer);
  }, []);

  // Een volle quota is de reden dat een capture stil kan mislukken, dus dat
  // hoort in hetzelfde signaal en niet in een apart hoekje.
  useEffect(() => {
    let gestopt = false;
    const meet = async () => {
      const ruimte = await getOpslagRuimte();
      if (!gestopt) setOpslagVol((ruimte?.aandeel ?? 0) > 0.9);
    };
    void meet();
    const timer = setInterval(() => void meet(), TIK_MS);
    return () => {
      gestopt = true;
      clearInterval(timer);
    };
  }, []);

  if (!stand || stand.aantal === 0) return null;

  const blijftHangen =
    stand.gefaald > 0 ||
    opslagVol ||
    (stand.oudsteMs !== null && stand.oudsteMs > WAARSCHUW_VANAF_MS);

  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1.5 rounded-wb px-2 py-1 text-xs font-bold uppercase tracking-wide font-mono',
        blijftHangen ? 'bg-wb-geld text-wb-geldink' : 'bg-white/20 text-wb-kopink'
      )}
      title={
        opslagVol
          ? "Het toestel zit bijna vol EN er staan foto's te wachten. Nieuwe foto's kunnen mislukken. Blijf op wifi met de app open tot de teller op nul staat."
          : blijftHangen
            ? "Deze foto's staan nog op dit toestel en zijn nergens anders bewaard. Blijf op wifi met de app open tot de teller op nul staat."
            : "Foto's worden geüpload"
      }
    >
      <span
        className={clsx(
          'w-2 h-2 rounded-full',
          blijftHangen ? 'bg-wb-geldink' : 'bg-wb-mint'
        )}
      />
      {stand.aantal} foto{stand.aantal === 1 ? '' : "'s"}
      {/* opslagVol als eerste: een vol toestel is de oorzaak achter de rest
          (uploads breken, iOS gooit foto-data weg), en een title-tooltip
          bestaat op een telefoon niet — dit moet in de zichtbare tekst. */}
      {opslagVol
        ? ' — toestel vol, ruimte vrijmaken!'
        : stand.gefaald > 0
          ? ` — ${stand.gefaald} mislukt, meld bij kantoor`
          : blijftHangen
            ? ' — blijf online'
            : ''}
    </span>
  );
}
